import {
  Backdrop,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  FormControlLabel,
  FormHelperText,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import * as Yup from 'yup';
import { DatePicker } from '@mui/lab';
import { useSnackbar } from 'notistack';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { useMemo, useState } from 'react';
import getFileData from 'src/utils/getFileData';
import { getFhirIdFromEntity } from 'src/utils/fhir';
import { UploadFiles } from 'src/careflow/common';
import { yupResolver } from '@hookform/resolvers/yup';
import { useOrganizations } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { InsuredOptionProps } from 'src/@types/crs/referral';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { Controller, SubmitHandler, useForm, useFormContext } from 'react-hook-form';
import { DocumentReference } from 'src/careflow/ph-core/fhir-client/resources';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { isEqual } from 'lodash';
import {
  addDocumentRelationships,
  convertBlobToBase64,
  COVERAGE_CLASS_CODE,
  COVERAGE_CLASS_SYSTEM,
  getCoveragePart,
  getCoverageReferences,
  getRelatedReferences,
  getResourceReference,
  hasAllReferences,
  mapDocumentReferenceToResource,
  MEDICARE_PAYOR_DISPLAY,
  PARTS,
  removeDocumentRelationships,
} from './helpers';
import { i18nFunction } from 'src/careflow/ph-core/internationalization/hook/useLocales';

interface Parts {
  partChecked: string | undefined;
  partDate?: Date;
}

type FormValues = {
  insuranceId: string;
  insuranceCompany: string;
  parts: Parts[];
  files: Blob[];
  documents: string[];
};

type Props = InsuredOptionProps;

export default function Medicare({
  patient,
  documentReference,
  coverage,
  serviceRequest,
  handleCreateBynary,
  handleCoverage,
  handleCreateDocumentReference,
  handleUpdateDocumentReference,
  handleUpdateResource,
  onCancel,
  i18n,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const authUser = useAuth().getCurrentUser();
  const { configurations } = useTenantConfigData();
  const [organization] = useOrganizations();

  const [openBackdrop, setOpenBackdrop] = useState(false);

  /**
   * --------------------------------------------------------------------------
   * Validation
   * --------------------------------------------------------------------------
   */
  i18n('patients.details.insurance.create', 'crs');
  const EventSchema = Yup.object({
    insuranceId: Yup.string().required(
      i18n('patients.details.insurance.medicareIdRequired', 'crs')
    ),

    parts: Yup.array()
      .of(
        Yup.object({
          partChecked: Yup.string().optional(),
          partDate: Yup.date().optional(),
        })
      )
      .test(
        'part_checked_test',
        i18n('patients.details.insurance.partRequired', 'crs'),
        (parts) => parts?.some((part) => part.partChecked !== undefined) ?? false
      ),
  });

  /**
   * --------------------------------------------------------------------------
   * Form defaults
   * --------------------------------------------------------------------------
   */

  const edit = Boolean(coverage?.id);

  const coveragePart = getCoveragePart(coverage ?? undefined);

  const coveragePartIndex = coveragePart
    ? PARTS.findIndex((part) => part.value === coveragePart)
    : -1;

  const partEdit: Parts[] =
    coveragePartIndex >= 0
      ? PARTS.slice(0, coveragePartIndex + 1).map((_, index) => ({
          partChecked: index === coveragePartIndex ? coveragePart : undefined,
          partDate:
            index === coveragePartIndex && coverage?.period?.start
              ? new Date(coverage.period.start)
              : undefined,
        }))
      : [];

  /**
   * --------------------------------------------------------------------------
   * Related documents
   * --------------------------------------------------------------------------
   *
   * If both ServiceRequest and Coverage exist, a document is considered
   * related only when it contains both references.
   */

  const documentsRelated = useMemo<string[]>(() => {
    const serviceRequestReference = getResourceReference(serviceRequest);

    const coverageReference = getResourceReference(coverage);

    const requiredReferences = [serviceRequestReference, coverageReference].filter(
      (reference): reference is string => Boolean(reference)
    );

    if (!requiredReferences.length) {
      return [];
    }

    return (
      documentReference
        ?.filter((document) => {
          const related = document.context?.related ?? [];

          return hasAllReferences(related, requiredReferences);
        })
        .map((document) => document.id)
        .filter((id): id is string => Boolean(id)) ?? []
    );
  }, [documentReference, serviceRequest, coverage]);

  const defaultValues = useMemo(
    () => ({
      insuranceId: coverage?.subscriberId || '',
      parts: partEdit,
      documents: documentsRelated,
    }),
    [coverage, documentsRelated, partEdit]
  );

  const methods = useForm<FormValues>({
    resolver: yupResolver(EventSchema),
    defaultValues,
  });

  const { watch, handleSubmit } = methods;

  const parts = watch('parts');

  /**
   * --------------------------------------------------------------------------
   * Binary helpers
   * --------------------------------------------------------------------------
   */
  const createBinaryCustom = async (data: FormValues): Promise<any[]> => {
    if (!data.files?.length) {
      return [];
    }

    return Promise.all(
      data.files.map(async (file: Blob) => {
        const base64 = await convertBlobToBase64(file);
        const { name } = getFileData(Object.assign(file));

        const binary = await handleCreateBynary({
          resourceType: 'Binary',
          contentType: file.type,
          securityContext: {
            reference: getResourceReference(patient),
          },
          data: base64,
        });

        return {
          ...binary[0],
          name,
        };
      })
    );
  };

  /**
   * --------------------------------------------------------------------------
   * DocumentReference creation
   * --------------------------------------------------------------------------
   */

  const getOrganizationReference = () => {
    const selectedOrganization = organization?.find(
      (item) => item.id === authUser.organization_fhir_uri
    );

    if (!selectedOrganization) {
      return undefined;
    }

    return {
      display: selectedOrganization.name,
      reference: `Organization/${selectedOrganization.id}`,
    };
  };

  const getAuthorReference = () => {
    const userFhirUri = authUser.user_fhir_uri?.trim();

    if (!userFhirUri) {
      return {
        display: authUser.name,
      };
    }

    return {
      reference: `Practitioner/${getFhirIdFromEntity(userFhirUri)}`,
      display: authUser.name,
    };
  };

  const createDocumentReference = async (binaryList: any[]): Promise<DocumentReference[]> => {
    if (!binaryList.length) {
      return [];
    }

    const organizationFhir = getOrganizationReference();

    const patientReference = getResourceReference(patient);

    const now = new Date().toISOString();

    const documentReferences = binaryList.map((binary) => ({
      resourceType: 'DocumentReference',
      status: 'current',

      subject: {
        reference: patientReference,
      },

      author: [getAuthorReference()],

      ...(organizationFhir && {
        custodian: organizationFhir,
      }),

      date: now,

      content: [
        {
          attachment: {
            contentType: binary.contentType,
            creation: now,
            title: binary.name,
            url: `${configurations?.fhir?.baseUrl}/Binary/${binary.id}`,
          },
        },
      ],
    }));

    return (await handleCreateDocumentReference(documentReferences)) ?? [];
  };

  /**
   * --------------------------------------------------------------------------
   * Coverage
   * --------------------------------------------------------------------------
   */

  const mapCoverage = (data: FormValues): any =>
    data.parts
      .filter((part) => part.partChecked !== undefined)
      .map((part) => ({
        resourceType: 'Coverage',

        ...(coverage?.id && {
          id: coverage.id,
        }),

        status: 'active',

        subscriberId: data.insuranceId,

        beneficiary: {
          reference: getResourceReference(patient),
        },

        payor: [
          {
            display: MEDICARE_PAYOR_DISPLAY,
          },
        ],

        class: [
          {
            type: {
              coding: [
                {
                  system: COVERAGE_CLASS_SYSTEM,
                  code: COVERAGE_CLASS_CODE,
                },
              ],
            },

            value: 'medicare',

            name: `Medicare Part ${part.partChecked}`,
          },
        ],

        period: {
          start: part.partDate,
        },
      }));

  /**
   * --------------------------------------------------------------------------
   * DocumentReference mapping
   * --------------------------------------------------------------------------
   */

  const updateDocumentRelationships = (
    documents: FormValues['documents'],
    createdDocuments: DocumentReference[] = [],
    resourceToRelate?: any
  ) => {
    const selectedDocumentIds = new Set(documents ?? []);

    const previousDocumentIds = new Set(documentsRelated);

    const documentsToRemove = new Set(
      documentsRelated.filter((documentId) => !selectedDocumentIds.has(documentId))
    );

    const documentsToAdd = new Set(
      Array.from(selectedDocumentIds).filter((documentId) => !previousDocumentIds.has(documentId))
    );

    const removedDocuments =
      documentReference
        ?.filter((document) => (document.id ? documentsToRemove.has(document.id) : false))
        .map((document) => removeDocumentRelationships(document, resourceToRelate)) ?? [];

    const selectedDocuments =
      documentReference
        ?.filter((document) => (document.id ? documentsToAdd.has(document.id) : false))
        .map((document) => addDocumentRelationships(document, resourceToRelate)) ?? [];

    const newDocuments = createdDocuments.map((document) =>
      addDocumentRelationships(document, resourceToRelate)
    );

    return [...removedDocuments, ...selectedDocuments, ...newDocuments];
  };

  /**
   * --------------------------------------------------------------------------
   * Document change detection
   * --------------------------------------------------------------------------
   */

  const hasDocumentsChanged = (data: FormValues): boolean =>
    !isEqual(data.documents ?? [], documentsRelated) || Boolean(data.files?.length);

  /**
   * --------------------------------------------------------------------------
   * Document update flow
   * --------------------------------------------------------------------------
   */

  const updateDocumentsSelected = async (
    data: FormValues,
    createdDocs: DocumentReference[] = [],
    resourceToRelate?: any
  ): Promise<DocumentReference[]> => {
    const selectedDocuments = data.documents ?? [];

    const requiredReferences = getRelatedReferences(resourceToRelate, serviceRequest);

    const allUpdatedDocuments = updateDocumentRelationships(
      selectedDocuments,
      createdDocs,
      requiredReferences
    );

    if (allUpdatedDocuments.length > 0) {
      await handleUpdateDocumentReference(allUpdatedDocuments);
    }

    /**
     * ServiceRequest.supportingInfo should contain
     * the documents that remain related to the
     * ServiceRequest.
     */
    if (serviceRequest && typeof handleUpdateResource === 'function') {
      const removedDocuments = allUpdatedDocuments.filter((document) => {
        const removedIds = new Set(
          documentsRelated.filter((id) => !selectedDocuments.includes(id))
        );

        return Boolean(document.id && removedIds.has(document.id));
      });

      const selectedAndCreatedDocuments = allUpdatedDocuments.filter((document) => {
        const documentId = document.id;

        return documentId && selectedDocuments.includes(documentId);
      });

      const coverageReferences = getCoverageReferences(resourceToRelate);

      const supportingInfo = mapDocumentReferenceToResource(
        serviceRequest,
        [...selectedAndCreatedDocuments, ...createdDocs],
        removedDocuments
      );

      const updatedServiceRequest = {
        ...serviceRequest,
        ...(supportingInfo.length > 0
          ? {
              supportingInfo,
            }
          : {
              supportingInfo: undefined,
            }),

        ...(coverageReferences.length > 0
          ? {
              insurance: coverageReferences,
            }
          : {
              insurance: undefined,
            }),
      };

      if (!supportingInfo.length) {
        delete updatedServiceRequest.supportingInfo;
      }

      if (!coverageReferences.length) {
        delete updatedServiceRequest.insurance;
      }

      handleUpdateResource(updatedServiceRequest);
    }

    return allUpdatedDocuments;
  };

  /**
   * --------------------------------------------------------------------------
   * Submit
   * --------------------------------------------------------------------------
   */

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setOpenBackdrop(true);

    try {
      /**
       * 1. Create Binary resources
       */
      const binaryList = await createBinaryCustom(data);

      /**
       * 2. Create DocumentReference resources
       */
      const createdDocuments =
        binaryList.length > 0 ? await createDocumentReference(binaryList) : [];

      /**
       * 3. Create/update Coverage
       */
      const coverageResources = mapCoverage(data);

      let resourceToRelate: any;

      if (coverageResources.length > 0) {
        resourceToRelate = await handleCoverage(coverageResources);
      }

      /**
       * 4. Update ServiceRequest
       *
       * This needs to happen when:
       * - documents changed
       * - new documents were created
       * - coverage was created/updated
       */
      const shouldUpdateServiceRequest =
        hasDocumentsChanged(data) || createdDocuments.length > 0 || coverageResources.length > 0;

      if (shouldUpdateServiceRequest) {
        await updateDocumentsSelected(data, createdDocuments, resourceToRelate);
      }

      onCancel();
    } catch (error) {
      console.error('Error saving Medicare information:', error);

      enqueueSnackbar(i18n('errorMessages.error', 'crs'), {
        variant: 'error',
      });
    } finally {
      setOpenBackdrop(false);
    }
  };

  /**
   * --------------------------------------------------------------------------
   * Render
   * --------------------------------------------------------------------------
   */

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={openBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Grid container spacing={3} sx={{ p: 2 }}>
        <Grid item xs={6}>
          <Stack direction="column" justifyContent="space-between">
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                py: 1,
              }}
            >
              {`${i18n('patients.details.insurance.medicare', 'crs')}*`}
            </Typography>

            <RHFTextField name="insuranceId" />
          </Stack>
        </Grid>

        <MedicareParts parts={parts ?? []} edit={edit} i18n={i18n} />
      </Grid>

      <Typography
        variant="body2"
        sx={{
          mt: 1,
          ml: 2,
        }}
      >
        {i18n('patients.details.insurance.medicareCard', 'crs')}
      </Typography>

      <UploadFiles
        label="insurance"
        restrictFiles
        documentReferences={documentReference}
        patient={patient}
        docRelated={documentsRelated}
      />

      <Stack alignItems="center">
        <DialogActions>
          <Button variant="outlined" color="info" type="submit">
            {i18n('saveChanges')}
          </Button>
        </DialogActions>
      </Stack>
    </FormProvider>
  );
}

/**
 * =============================================================================
 * MedicareParts
 * =============================================================================
 */

type MedicarePartsProp = {
  parts: Parts[];
  edit: boolean;
  i18n: i18nFunction;
};

function MedicareParts({ parts, edit, i18n }: MedicarePartsProp) {
  const { control, formState } = useFormContext<FormValues>();
  const partsError = (formState.errors.parts as any) ?? {};
  const partsErrorMessage =
    typeof partsError?.message === 'string' ? partsError.message : undefined;

  return (
    <>
      <Grid item xs={6}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            pt: 1,
          }}
        >
          {`${i18n('patients.details.insurance.medicarePart', 'crs')}*`}
        </Typography>

        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            width: 1,
            pt: 2,
            '& .MuiFormGroup-root': {
              flexDirection: 'row',
            },
          }}
        >
          {PARTS.map((part, index) => (
            <FormControlLabel
              key={part.value}
              control={
                <Controller
                  name={`parts.${index}.partChecked`}
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value !== undefined}
                      disabled={edit}
                      value={part.value}
                      onChange={() => {
                        field.onChange(part.value !== field.value ? part.value : undefined);
                      }}
                    />
                  )}
                />
              }
              disabled={edit}
              label={part.label}
            />
          ))}
        </Stack>

        {partsErrorMessage && (
          <FormHelperText
            sx={{
              color: '#FF4842',
            }}
          >
            {partsErrorMessage}
          </FormHelperText>
        )}
      </Grid>

      <Grid item xs={12}>
        <Stack direction="row">
          {parts.map(
            (part, index) =>
              part.partChecked && (
                <Stack key={part.partChecked} direction="column" sx={{ px: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      py: 1,
                    }}
                  >
                    {`${i18n('patients.details.insurance.coverage', 'crs')} ${
                      part.partChecked
                    } ${i18n('patients.details.insurance.starts', 'crs')}`}
                  </Typography>

                  <Controller
                    name={`parts.${index}.partDate`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <DatePicker
                        value={field.value ?? null}
                        onChange={(newValue) => {
                          field.onChange(newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            error={Boolean(error)}
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Stack>
              )
          )}
        </Stack>
      </Grid>
    </>
  );
}
